import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import {
  FileText,
  Upload,
  Trash2,
  Download,
  Edit2,
  Save,
  X,
  Loader2,
  Files,
  MessageCircle,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Separator } from "@/components/ui/separator";
import { documentsApi } from "@/lib/crmApi";

function humanSize(bytes) {
  if (!bytes) return "0 KB";
  if (bytes < 1024) return bytes + " B";
  if (bytes < 1024 * 1024) return Math.round(bytes / 1024) + " KB";
  return (bytes / 1024 / 1024).toFixed(1) + " MB";
}

export default function DocumentsLibraryPanel({ projetId = null, projetNom = "" }) {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      // Scope to the active project: show its own documents + the globals
      // (docs with no projet_id). Uploads made here are tagged to this project.
      setDocs(
        await documentsApi.list(
          projetId ? { projetId, includeGlobal: true } : {}
        )
      );
    } catch (e) {
      toast.error("Erreur de chargement");
    } finally {
      setLoading(false);
    }
  }, [projetId]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <section
      className="rounded-2xl border border-border/60 bg-card p-5 shadow-soft sm:p-6 animate-fade-in"
      data-testid="section-documents-library"
    >
      <div className="mb-5 flex flex-col items-start justify-between gap-3 sm:flex-row sm:items-center">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-blue-50 text-blue-700 dark:bg-blue-950/40 dark:text-blue-300">
            <Files className="h-5 w-5" />
          </div>
          <div>
            <h2 className="font-heading text-lg font-bold">
              Bibliothèque de documents
              {projetNom && (
                <span className="ml-2 text-sm font-normal text-muted-foreground">
                  · {projetNom}
                </span>
              )}
            </h2>
            <p className="text-xs text-muted-foreground">
              {docs.length} document{docs.length > 1 ? "s" : ""} disponible{docs.length > 1 ? "s" : ""}
              {projetNom && " (projet + documents communs)"}
            </p>
          </div>
        </div>
        <Button
          size="sm"
          onClick={() => setShowForm(true)}
          className="gap-1.5"
          data-testid="document-upload-btn"
        >
          <Upload className="h-4 w-4" />
          Téléverser un document
        </Button>
      </div>

      {/* Hint vers la config welcome (la sélection des docs auto se fait là-bas) */}
      <div className="mb-4 flex items-start gap-2 rounded-lg border border-blue-200 bg-blue-50/40 px-3 py-2 text-xs dark:border-blue-900/50 dark:bg-blue-950/20">
        <MessageCircle className="mt-0.5 h-3.5 w-3.5 flex-none text-blue-700 dark:text-blue-300" />
        <p className="text-blue-900 dark:text-blue-200">
          Pour choisir les documents envoyés automatiquement à chaque nouveau prospect,
          va dans <b>Paramètres → WhatsApp → Message de bienvenue</b>.
          Cette page sert juste à téléverser et gérer les fichiers.
        </p>
      </div>

      <Separator className="mb-4" />

      {showForm && (
        <UploadForm
          projetId={projetId}
          onCancel={() => setShowForm(false)}
          onUploaded={() => {
            setShowForm(false);
            load();
          }}
        />
      )}

      <div className="space-y-2">
        {loading && docs.length === 0 ? (
          <div className="rounded-xl border border-dashed border-border/60 p-6 text-center text-sm text-muted-foreground">
            Chargement…
          </div>
        ) : docs.length === 0 ? (
          <div className="rounded-xl border border-dashed border-border/60 p-6 text-center">
            <FileText className="mx-auto h-8 w-8 text-muted-foreground/40" />
            <p className="mt-2 text-sm font-semibold">Aucun document</p>
            <p className="mt-1 text-xs text-muted-foreground">
              Téléversez vos catalogues, plans, brochures ou conditions de
              financement. Vous pourrez ensuite les attacher au message de
              bienvenue ou les envoyer manuellement à un lead.
            </p>
          </div>
        ) : (
          docs.map((d) => <DocRow key={d.id} doc={d} onChanged={load} />)
        )}
      </div>
    </section>
  );
}

/* ---------------------------------------------------------------- */

function DocRow({ doc, onChanged }) {
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    nom: doc.nom,
    description: doc.description || "",
    caption: doc.caption || "",
  });

  const save = async () => {
    if (!form.nom.trim()) return toast.error("Nom requis");
    setBusy(true);
    try {
      await documentsApi.update(doc.id, form);
      toast.success("Document mis à jour");
      setEditing(false);
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    } finally {
      setBusy(false);
    }
  };

  const onDelete = async () => {
    if (!window.confirm(`Supprimer "${doc.nom}" ? (action irréversible)`)) return;
    setBusy(true);
    try {
      await documentsApi.remove(doc.id);
      toast.success("Document supprimé");
      onChanged?.();
    } catch (e) {
      toast.error("Erreur lors de la suppression");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="rounded-xl border border-border/60 bg-card p-3 transition-colors hover:bg-muted/30"
      data-testid={`document-row-${doc.id}`}
    >
      {editing ? (
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label className="text-[11px]">Nom</Label>
            <Input
              value={form.nom}
              onChange={(e) => setForm({ ...form, nom: e.target.value })}
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-[11px]">Description</Label>
            <Input
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              placeholder="Optionnel"
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-[11px]">
              Message WhatsApp d'accompagnement (optionnel)
            </Label>
            <Textarea
              rows={3}
              value={form.caption}
              onChange={(e) => setForm({ ...form, caption: e.target.value })}
              placeholder="Variables : {nom}, {prenom}"
            />
          </div>
          <div className="flex justify-end gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setEditing(false)}
              disabled={busy}
            >
              <X className="mr-1 h-3 w-3" /> Annuler
            </Button>
            <Button size="sm" onClick={save} disabled={busy} className="gap-1.5">
              {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Save className="h-3 w-3" />}
              Enregistrer
            </Button>
          </div>
        </div>
      ) : (
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <FileText className="h-4 w-4 flex-none text-blue-600 dark:text-blue-300" />
              <span className="font-heading font-semibold">{doc.nom}</span>
            </div>
            <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[11px] text-muted-foreground">
              <span>{doc.filename}</span>
              <span>·</span>
              <span>{humanSize(doc.size)}</span>
              <span>·</span>
              <span className="lowercase">{doc.mime}</span>
            </div>
            {doc.description && (
              <p className="mt-1 text-xs text-muted-foreground">{doc.description}</p>
            )}
            {doc.caption && (
              <p className="mt-1 line-clamp-2 rounded bg-muted/40 px-2 py-1 text-[11px] italic">
                « {doc.caption} »
              </p>
            )}
          </div>

          <div className="flex items-center gap-1">
            <Button
              size="icon"
              variant="ghost"
              className="h-7 w-7"
              onClick={async () => {
                try {
                  await documentsApi.download(doc.id, doc.filename || doc.nom);
                } catch (e) {
                  toast.error(e?.humanMessage || "Échec du téléchargement");
                }
              }}
              title="Télécharger"
              data-testid={`document-download-${doc.id}`}
            >
              <Download className="h-3.5 w-3.5" />
            </Button>
            <Button
              size="icon"
              variant="ghost"
              className="h-7 w-7"
              onClick={() => setEditing(true)}
              title="Modifier"
              data-testid={`document-edit-${doc.id}`}
            >
              <Edit2 className="h-3.5 w-3.5" />
            </Button>
            <Button
              size="icon"
              variant="ghost"
              className="h-7 w-7 text-muted-foreground hover:text-red-500"
              onClick={onDelete}
              disabled={busy}
              title="Supprimer"
              data-testid={`document-delete-${doc.id}`}
            >
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

/* ---------------------------------------------------------------- */

function UploadForm({ onCancel, onUploaded, projetId = null }) {
  const inputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [form, setForm] = useState({
    nom: "",
    description: "",
    caption: "",
  });
  const [busy, setBusy] = useState(false);

  const onPick = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 50 * 1024 * 1024) {
      toast.error("Fichier trop volumineux (>50 MB)");
      e.target.value = "";
      return;
    }
    setFile(f);
    if (!form.nom) {
      setForm((s) => ({ ...s, nom: f.name }));
    }
  };

  const submit = async () => {
    if (!file) return toast.error("Choisissez un fichier");
    if (!form.nom.trim()) return toast.error("Nom requis");
    setBusy(true);
    try {
      // auto_send_on_new_lead defaults to false — selection now lives in
      // the Welcome settings (Paramètres → WhatsApp).
      await documentsApi.upload({
        file,
        nom: form.nom,
        description: form.description,
        caption: form.caption,
        auto_send_on_new_lead: false,
        projetId, // tag the document to the active project
      });
      toast.success("Document téléversé");
      onUploaded?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || "Erreur");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="mb-4 rounded-xl border-2 border-dashed border-blue-300 bg-blue-50/30 p-4 dark:border-blue-700/40 dark:bg-blue-950/10 animate-fade-in"
      data-testid="document-form"
    >
      <div className="mb-3 flex items-center justify-between">
        <h3 className="font-heading text-sm font-bold">Nouveau document</h3>
        <Button variant="ghost" size="icon" onClick={onCancel} className="h-7 w-7">
          <X className="h-3.5 w-3.5" />
        </Button>
      </div>

      <div className="space-y-3">
        <div>
          <input
            ref={inputRef}
            type="file"
            accept="application/pdf,image/png,image/jpeg,image/webp,video/mp4,video/3gpp,video/quicktime,.pdf,.png,.jpg,.jpeg,.webp,.docx,.doc,.pptx,.ppt,.mp4,.3gp,.mov"
            onChange={onPick}
            className="hidden"
          />
          <Button
            variant="outline"
            onClick={() => inputRef.current?.click()}
            className="w-full gap-2"
          >
            <Upload className="h-4 w-4" />
            {file ? `${file.name} (${humanSize(file.size)})` : "Choisir un fichier (PDF, image, Word, PPT, vidéo — max 50 MB)"}
          </Button>
        </div>

        <div className="grid gap-3 md:grid-cols-2">
          <div className="space-y-1.5">
            <Label className="text-[11px]">Nom affiché *</Label>
            <Input
              value={form.nom}
              onChange={(e) => setForm({ ...form, nom: e.target.value })}
              placeholder="Ex : Brochure RYAD F3"
              data-testid="document-form-nom"
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-[11px]">Description</Label>
            <Input
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              placeholder="Optionnel"
            />
          </div>
        </div>

        <div className="space-y-1.5">
          <Label className="text-[11px]">Message d'accompagnement (optionnel)</Label>
          <Textarea
            rows={3}
            value={form.caption}
            onChange={(e) => setForm({ ...form, caption: e.target.value })}
            placeholder={"Bonjour {prenom}, voici la documentation comme convenu."}
            data-testid="document-form-caption"
          />
          <p className="text-[10px] text-muted-foreground">
            Variables : <code className="rounded bg-muted px-1">{"{nom}"}</code>,{" "}
            <code className="rounded bg-muted px-1">{"{prenom}"}</code>
          </p>
        </div>

        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onCancel} disabled={busy}>
            Annuler
          </Button>
          <Button onClick={submit} disabled={busy} className="gap-1.5" data-testid="document-form-save">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
            Téléverser
          </Button>
        </div>
      </div>
    </div>
  );
}
